import { useEffect, useState } from "react";
import api from "../api";
import { useI18n } from "../i18n";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { eur } from "../helpers";
import OwnerDetailDialog from "../components/OwnerDetailDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, KeyRound, Mail, Phone, Building2, FolderOpen } from "lucide-react";

const empty = { name: "", email: "", phone: "", whatsapp: "", iban: "", commission_pct: 0, management_type: "commissione", monthly_rent: 0, deposit: 0, contract_start: "", contract_end: "", auto_renew: false, documents: [], notes: "" };

import { Field } from "../components/FormField";

export default function Owners() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [apts, setApts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [detailOwner, setDetailOwner] = useState(null);

  const load = () => api.get("/owners").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/apartments").then((r) => setApts(r.data)); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setForm(empty); setOpen(true); };
  const openEdit = (it) => { setForm({ ...empty, ...it }); setOpen(true); };
  const aptCount = (oid) => apts.filter((a) => a.owner_id === oid).length;

  const save = async () => {
    if (!form.name.trim()) { toast.error(t("ownerName")); return; }
    try {
      if (form.id) await api.put(`/owners/${form.id}`, form);
      else await api.post("/owners", form);
      toast.success(t("saved")); setOpen(false); load();
    } catch { toast.error("Error"); }
  };
  const del = async (id) => { if (!window.confirm(t("confirm"))) return; await api.delete(`/owners/${id}`); toast.success(t("deleted")); load(); };

  return (
    <div className="space-y-6 fade-in" data-testid="owners-page">
      <div className="flex justify-end">
        <Button data-testid="add-owner-btn" onClick={openNew} className="gap-2 bg-primary hover:bg-primary/90"><Plus size={18} /> {t("add")}</Button>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
        {items.map((o) => (
          <Card key={o.id} data-testid={`owner-card-${o.id}`} className="border-border p-6 transition-all hover:-translate-y-1 hover:shadow-md">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary"><KeyRound size={18} /></div>
                <div>
                  <h3 className="font-head font-semibold text-slate-900">{o.name}</h3>
                  <p className="flex items-center gap-1 text-xs text-slate-500"><Building2 size={12} /> {aptCount(o.id)} {t("apartments").toLowerCase()}</p>
                </div>
              </div>
              <span className="rounded-full bg-secondary px-2.5 py-0.5 text-xs font-semibold text-primary">{o.commission_pct}%</span>
            </div>
            <div className="mt-4 space-y-1.5 text-xs text-slate-600">
              {o.email && <div className="flex items-center gap-2"><Mail size={13} /> {o.email}</div>}
              {o.phone && <div className="flex items-center gap-2"><Phone size={13} /> {o.phone}</div>}
              {o.iban && <div className="font-mono text-[11px] text-slate-500">{o.iban}</div>}
            </div>
            {o.notes && <p className="mt-3 text-xs text-slate-500">{o.notes}</p>}
            <div className="mt-4 flex gap-2">
              <Button data-testid={`detail-owner-${o.id}`} size="sm" className="flex-1 gap-1 bg-primary hover:bg-primary/90" onClick={() => setDetailOwner(o)}><FolderOpen size={14} /> Dettaglio</Button>
              <Button data-testid={`edit-owner-${o.id}`} variant="outline" size="sm" className="gap-1" onClick={() => openEdit(o)}><Pencil size={14} /></Button>
              <Button data-testid={`delete-owner-${o.id}`} variant="outline" size="sm" className="text-rose-600" onClick={() => del(o.id)}><Trash2 size={14} /></Button>
            </div>
          </Card>
        ))}
        {items.length === 0 && <p className="text-sm text-slate-400">{t("noData")}</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader><DialogTitle>{form.id ? t("edit") : t("add")} · {t("owners")}</DialogTitle><DialogDescription>{t("owners")}</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("ownerName")} className="col-span-2"><Input data-testid="owner-name" value={form.name} onChange={(e) => set("name", e.target.value)} /></Field>
            <Field label={t("email")}><Input value={form.email} onChange={(e) => set("email", e.target.value)} /></Field>
            <Field label={t("phone")}><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} /></Field>
            <Field label="WhatsApp"><Input value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} placeholder="+39…" /></Field>
            <Field label={t("iban")}><Input value={form.iban} onChange={(e) => set("iban", e.target.value)} /></Field>
            <div className="col-span-2 mt-1 border-t border-slate-200 pt-3 text-xs font-semibold uppercase text-slate-400">Dati economici</div>
            <Field label="Tipologia gestione">
              <Select value={form.management_type} onValueChange={(v) => set("management_type", v)}>
                <SelectTrigger data-testid="owner-mgmt-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="commissione">Commissione %</SelectItem>
                  <SelectItem value="canone_fisso">Canone fisso</SelectItem>
                  <SelectItem value="mista">Gestione mista</SelectItem>
                  <SelectItem value="revenue_share">Revenue share</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Commissione %"><Input data-testid="owner-commission" type="number" value={form.commission_pct} onChange={(e) => set("commission_pct", +e.target.value)} /></Field>
            <Field label="Canone affitto mensile (€)"><Input data-testid="owner-rent" type="number" value={form.monthly_rent} onChange={(e) => set("monthly_rent", +e.target.value)} /></Field>
            <Field label="Deposito cauzionale (€)"><Input type="number" value={form.deposit} onChange={(e) => set("deposit", +e.target.value)} /></Field>
            <Field label="Inizio contratto"><Input type="date" value={form.contract_start} onChange={(e) => set("contract_start", e.target.value)} /></Field>
            <Field label="Scadenza contratto"><Input type="date" value={form.contract_end} onChange={(e) => set("contract_end", e.target.value)} /></Field>
            <label className="col-span-2 flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" data-testid="owner-autorenew" checked={!!form.auto_renew} onChange={(e) => set("auto_renew", e.target.checked)} /> Rinnovo automatico</label>
            <Field label={t("notes")} className="col-span-2"><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button data-testid="save-owner-btn" onClick={save} className="bg-primary hover:bg-primary/90">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <OwnerDetailDialog open={!!detailOwner} onOpenChange={(v) => !v && setDetailOwner(null)} owner={detailOwner} apts={apts} onSaved={load} />
    </div>
  );
}
